// src/constants/featureFlags.js
// MyClips — runtime feature flag'leri. Default değerler güvenli (mevcut
// prod davranışını koruyan) olmalı. Admin/tester aşağıdaki yollarla
// geçici olarak açabilir:
//   - localStorage (tek sekme scope): localStorage.setItem('mylasa.ff.<key>', 'value')
//   - Build-time env var: REACT_APP_FF_<KEY> = value
// Öncelik sırası: localStorage > env > default.

// ─── MyClips: feed sıralama modu ──────────────────────────────────────────
// "tarih"            → mevcut davranış (kronolojik). Safe default.
// "engagementScore"  → Faz 1B Görev 3'te yazılan skorla sıralar.
//                       NOT: legacy clipler engagementScore alanı yoksa
//                       sorguya girmez. Admin önce rerankClipsCallable
//                       çalıştırmalı, sonra flag'i açmalı.
const FEED_RANK_BY_VALID = new Set(["tarih", "engagementScore"]);
const FEED_RANK_BY_DEFAULT = "tarih";

function _readLocalStorageKey(key: string): string | null {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    const v = window.localStorage.getItem(`mylasa.ff.${key}`);
    return v == null ? null : String(v);
  } catch {
    return null;
  }
}

function _readEnvKey(key: string): string | null {
  try {
    const envKey = `REACT_APP_FF_${key.toUpperCase()}`;
    const v = process.env[envKey];
    return v == null || v === "" ? null : String(v);
  } catch {
    return null;
  }
}

/**
 * MyClips feed sıralaması hangi alana göre?
 * @returns {"tarih"|"engagementScore"}
 */
export function getClipsFeedRankBy() {
  const raw =
    _readLocalStorageKey("clipsFeedRankBy") ||
    _readEnvKey("clipsFeedRankBy") ||
    FEED_RANK_BY_DEFAULT;
  return FEED_RANK_BY_VALID.has(raw) ? raw : FEED_RANK_BY_DEFAULT;
}

// Geliştirme/test: global hook — console'dan değişiklik kolaylığı
if (typeof window !== "undefined") {
  (window as any).mylasaSetClipsFeedRankBy = (value: unknown) => {
    if (!FEED_RANK_BY_VALID.has(String(value))) {
      console.warn(`[FF] Geçersiz değer: ${value}. İzinli: tarih | engagementScore`);
      return;
    }
    try {
      window.localStorage.setItem("mylasa.ff.clipsFeedRankBy", String(value));
    } catch (e) {
      console.warn("[FF] localStorage yazılamadı", e);
    }
  };
}

// ─── MyClips: Creator dashboard görünürlüğü ───────────────────────────────
// Faz 1B Görev 7. Varsayılan açık (analytics verisi zaten yazılıyor).
// Kill-switch: "off" set edilirse CreatorDashboard sayfası Feed'e redirect.
// Kullanım:
//   - localStorage.setItem('mylasa.ff.creatorDashboard', 'off')  → kapalı
//   - localStorage.setItem('mylasa.ff.creatorDashboard', 'on')   → açık
const CREATOR_DASHBOARD_VALID = new Set(["on", "off"]);
const CREATOR_DASHBOARD_DEFAULT = "on";

/**
 * Creator dashboard açık mı?
 * @returns {boolean}
 */
export function getCreatorDashboardEnabled() {
  const raw =
    _readLocalStorageKey("creatorDashboard") ||
    _readEnvKey("creatorDashboard") ||
    CREATOR_DASHBOARD_DEFAULT;
  const val = CREATOR_DASHBOARD_VALID.has(raw) ? raw : CREATOR_DASHBOARD_DEFAULT;
  return val === "on";
}

if (typeof window !== "undefined") {
  (window as any).mylasaSetCreatorDashboard = (value: unknown) => {
    const v = String(value || "").toLowerCase();
    if (!CREATOR_DASHBOARD_VALID.has(v)) {
      console.warn(`[FF] Geçersiz değer: ${value}. İzinli: on | off`);
      return;
    }
    try {
      window.localStorage.setItem("mylasa.ff.creatorDashboard", v);
    } catch (e) {
      console.warn("[FF] localStorage yazılamadı", e);
    }
  };
}

// ─── FAZ 1C GÖREV 1: Clip yorum saklama modu ─────────────────────────────
// Hedef: Yorumları `clips/{id}.yorumlar` ARRAY'inden Firestore subcollection'a
// taşımak. Migrasyon boyunca üç mod desteklenir:
//   - "inline"        → sadece eski inline array (rollback için)
//   - "dual"          → SAFE DEFAULT — hem inline hem subcollection yazılır
//   - "subcollection" → sadece subcollection (Görev 3 backfill sonrası açılır)
// Okuma: "subcollection" modunda subcollection'dan, aksi halde inline'dan.
// Dual moddayken yeni yorumlar iki yerde de görünür — okuyucu fark etmez.
const CLIP_COMMENTS_STORAGE_VALID = new Set(["inline", "dual", "subcollection"]);
const CLIP_COMMENTS_STORAGE_DEFAULT = "subcollection"; // Faz 2 Pack 3: cutover — backfill tamamlandı

/**
 * Clip yorumları nereye yazılıp nereden okunacak?
 * @returns {"inline"|"dual"|"subcollection"}
 */
export function getClipCommentsStorage() {
  const raw =
    _readLocalStorageKey("clipCommentsStorage") ||
    _readEnvKey("clipCommentsStorage") ||
    CLIP_COMMENTS_STORAGE_DEFAULT;
  const val = String(raw).toLowerCase();
  return CLIP_COMMENTS_STORAGE_VALID.has(val) ? val : CLIP_COMMENTS_STORAGE_DEFAULT;
}

if (typeof window !== "undefined") {
  (window as any).mylasaSetClipCommentsStorage = (value: unknown) => {
    const v = String(value || "").toLowerCase();
    if (!CLIP_COMMENTS_STORAGE_VALID.has(v)) {
      console.warn(`[FF] Geçersiz değer: ${value}. İzinli: inline | dual | subcollection`);
      return;
    }
    try {
      window.localStorage.setItem("mylasa.ff.clipCommentsStorage", v);
    } catch (e) {
      console.warn("[FF] localStorage yazılamadı", e);
    }
  };
}

// ─── FAZ 1C GÖREV 5: Yorum moderasyonu hide davranışı ────────────────────
// Sunucu trigger `onCommentCreateModerate` rejected yorumlara `hidden:true`
// yazar (soft-delete). Client-side filter'in bu flag'e göre davranmasını
// istersek — operator bir düğmeyle açıp kapatabilir.
//   - "enforce" (default) → hidden=true olan yorumları listelerde gösterme
//   - "shadow"            → hidden=true olan yorumları yine göster
//                            (debugging / moderasyon kalitesi gözlemi için)
//   - "off"               → moderation durumunu hiç kontrol etme
const CLIP_COMMENT_MODERATION_VALID = new Set(["off", "shadow", "enforce"]);
const CLIP_COMMENT_MODERATION_DEFAULT = "enforce";

/**
 * Yorum moderasyon davranışı.
 * @returns {"off"|"shadow"|"enforce"}
 */
export function getClipCommentModeration() {
  const raw =
    _readLocalStorageKey("clipCommentModeration") ||
    _readEnvKey("clipCommentModeration") ||
    CLIP_COMMENT_MODERATION_DEFAULT;
  const val = String(raw).toLowerCase();
  return CLIP_COMMENT_MODERATION_VALID.has(val) ? val : CLIP_COMMENT_MODERATION_DEFAULT;
}

if (typeof window !== "undefined") {
  (window as any).mylasaSetClipCommentModeration = (value: unknown) => {
    const v = String(value || "").toLowerCase();
    if (!CLIP_COMMENT_MODERATION_VALID.has(v)) {
      console.warn(`[FF] Geçersiz değer: ${value}. İzinli: off | shadow | enforce`);
      return;
    }
    try {
      window.localStorage.setItem("mylasa.ff.clipCommentModeration", v);
    } catch (e) {
      console.warn("[FF] localStorage yazılamadı", e);
    }
  };
}

// ─── FAZ 1F GÖREV 3: Write-side engel enforcement modu ───────────────────
// Kullanıcının engellediği biriyle etkileşim (like / yorum / reply / follow)
// yazma anında durdurulur mu? Bildirim suppression zaten server-side (Faz 1F
// Görev 2) devrede — bu flag INTERAKSİYON YAZIMININ kendisini kontrol eder.
//
//   - "off"     → hiç kontrol yok (legacy davranış, acil rollback için).
//   - "shadow"  → SAFE DEFAULT — kontrol yapılır, engel varsa console'a
//                  log düşer ama yazım yine de gerçekleşir. Prod'da gözlem
//                  için; hangi uçlar tetikleniyor, ne sıklıkta — production
//                  veri olmadan enforce'a geçmek hatalı aksiyonları
//                  tespitleyemiyor olabilir.
//   - "on"      → enforce. Engellenen hedefe interaksiyon yazımı reddedilir;
//                  service fonksiyonları BLOCK_ENFORCED hatası fırlatır ya
//                  da skip eder. UI toast/alert için `message` alanı döner.
//
// Kademeli açılım planı:
//   1. Deploy ile "shadow" canlı → 24-48 saat console.info metriklerini
//      observe et (kaç interaksiyon engel durumuna giriyor).
//   2. Şüpheli bir artış yoksa `window.mylasaSetBlockEnforcement('on')`
//      → localStorage'da operatör tarafında enforce edilir.
//   3. Prod-geneli açmak için default'u "on" yap (kod değişikliği).
const BLOCK_ENFORCEMENT_VALID = new Set(["off", "shadow", "on"]);
// FAZ 2C — prod-geneli enforce: shadow gözlem dönemi tamamlandı; rules
// tarafında da ikinci katman var (yorum/DM create block kontrolü).
const BLOCK_ENFORCEMENT_DEFAULT = "on";

/**
 * Write-side block enforcement modu.
 * @returns {"off"|"shadow"|"on"}
 */
export function getBlockEnforcementMode() {
  const raw =
    _readLocalStorageKey("blockEnforcement") ||
    _readEnvKey("blockEnforcement") ||
    BLOCK_ENFORCEMENT_DEFAULT;
  const val = String(raw).toLowerCase();
  return BLOCK_ENFORCEMENT_VALID.has(val) ? val : BLOCK_ENFORCEMENT_DEFAULT;
}

if (typeof window !== "undefined") {
  (window as any).mylasaSetBlockEnforcement = (value: unknown) => {
    const v = String(value || "").toLowerCase();
    if (!BLOCK_ENFORCEMENT_VALID.has(v)) {
      console.warn(`[FF] Geçersiz değer: ${value}. İzinli: off | shadow | on`);
      return;
    }
    try {
      window.localStorage.setItem("mylasa.ff.blockEnforcement", v);
    } catch (e) {
      console.warn("[FF] localStorage yazılamadı", e);
    }
  };
}

// ─── Ana ev-feed ranking (chunked fetch + client-side skor) ────────────────
// "off" → BİREBİR bugünkü davranış (kronolojik, 30-takip cap'li, onSnapshot).
// "on"  → following listesi 30'luk parçalara bölünür (paralel getDocs → 30+
//         takipte de tüm yazarların içeriği gelir), merged item'lar client'ta
//         recency×engagement×yakınlık skoruyla sıralanır. Yeni Cloud Function/
//         index YOK; tamamen client-side. Geri alma: default "off".
// Maliyet uyarısı: ON modda açılış başına ~chunk_sayısı × (post+clip) sorgu.
// Bu yüzden default OFF + chunk üst sınırı (HOME_FEED_RANK_MAX_CHUNKS).
const HOME_FEED_RANK_VALID = new Set(["off", "on"]);
// Pre-launch (gerçek kullanıcı yok) → ranked feed yeni temel. Sorun olursa
// window.mylasaSetHomeFeedRank('off') + yenile ile anında geri alınır.
const HOME_FEED_RANK_DEFAULT = "on";

// ON modda kaç 30'luk yazar-parçası taranır (maliyet tavanı). 3 = ~90 yazar.
export const HOME_FEED_RANK_MAX_CHUNKS = 3;

/**
 * Ana ev-feed ranking açık mı?
 * @returns {boolean}
 */
export function getHomeFeedRankEnabled(): boolean {
  const raw =
    _readLocalStorageKey("homeFeedRank") ||
    _readEnvKey("homeFeedRank") ||
    HOME_FEED_RANK_DEFAULT;
  const val = String(raw).toLowerCase();
  const safe = HOME_FEED_RANK_VALID.has(val) ? val : HOME_FEED_RANK_DEFAULT;
  return safe === "on";
}

if (typeof window !== "undefined") {
  (window as any).mylasaSetHomeFeedRank = (value: unknown) => {
    const v = String(value || "").toLowerCase();
    if (!HOME_FEED_RANK_VALID.has(v)) {
      console.warn(`[FF] Geçersiz değer: ${value}. İzinli: off | on`);
      return;
    }
    try {
      window.localStorage.setItem("mylasa.ff.homeFeedRank", v);
      console.info(`[FF] homeFeedRank = ${v} (sayfayı yenile)`);
    } catch (e) {
      console.warn("[FF] localStorage yazılamadı", e);
    }
  };
}

// ─── Materialized HOME feed (fan-out-on-write) ───────────────────────────
// "off" → mevcut chunked-rank yolu (authorId in 30'luk parça + private filtre
//          + 3-chunk cap). "on" → users/{uid}/followingFeed materialized
//          koleksiyonundan oku (özel hesap gönderileri de takipçide görünür,
//          ~90 yazar cap'i kalkar). Boş/materialize-edilmemiş kullanıcıda
//          Feed.tsx otomatik chunked-rank yoluna FALLBACK eder (rawCount===0).
// Default "off" — fan-out trigger'ları deploy + backfill çalıştırılıp
// doğrulanana kadar güvenli. Test: window.mylasaSetHomeFeedMaterialized('on').
const HOME_FEED_MATERIALIZED_VALID = new Set(["off", "on"]);
// Backend (fan-out functions) deploy + mevcut gönderiler backfill edildi
// (315 followingFeed post girişi) + sahibi canlıda doğruladı → varsayılan "on".
// Boş/materialize-edilmemiş kullanıcı yine chunked-rank'a fallback eder
// (rawCount===0). Geri alma: window.mylasaSetHomeFeedMaterialized('off') + yenile.
const HOME_FEED_MATERIALIZED_DEFAULT = "on";

/**
 * Materialized home feed okuma yolu açık mı?
 * @returns {boolean}
 */
export function getHomeFeedMaterializedEnabled(): boolean {
  const raw =
    _readLocalStorageKey("homeFeedMaterialized") ||
    _readEnvKey("homeFeedMaterialized") ||
    HOME_FEED_MATERIALIZED_DEFAULT;
  const val = String(raw).toLowerCase();
  const safe = HOME_FEED_MATERIALIZED_VALID.has(val)
    ? val
    : HOME_FEED_MATERIALIZED_DEFAULT;
  return safe === "on";
}

if (typeof window !== "undefined") {
  (window as any).mylasaSetHomeFeedMaterialized = (value: unknown) => {
    const v = String(value || "").toLowerCase();
    if (!HOME_FEED_MATERIALIZED_VALID.has(v)) {
      console.warn(`[FF] Geçersiz değer: ${value}. İzinli: off | on`);
      return;
    }
    try {
      window.localStorage.setItem("mylasa.ff.homeFeedMaterialized", v);
      console.info(`[FF] homeFeedMaterialized = ${v} (sayfayı yenile)`);
    } catch (e) {
      console.warn("[FF] localStorage yazılamadı", e);
    }
  };
}

// ─── Keşfet (Explore) kişiselleştirme (client-side skor) ──────────────────
// "off" → mevcut kronolojik grid. "on" → yüklü grid recency×engagement×
// ilgi-etiketi ile client-side yeniden sıralanır (yeni sorgu/index YOK).
// Pre-launch → default "on" (IG-Explore paritesi). Geri alma: 'off' + yenile.
const EXPLORE_RANK_VALID = new Set(["off", "on"]);
const EXPLORE_RANK_DEFAULT = "on";

export function getExploreRankEnabled(): boolean {
  const raw =
    _readLocalStorageKey("exploreRank") ||
    _readEnvKey("exploreRank") ||
    EXPLORE_RANK_DEFAULT;
  const val = String(raw).toLowerCase();
  const safe = EXPLORE_RANK_VALID.has(val) ? val : EXPLORE_RANK_DEFAULT;
  return safe === "on";
}

if (typeof window !== "undefined") {
  (window as any).mylasaSetExploreRank = (value: unknown) => {
    const v = String(value || "").toLowerCase();
    if (!EXPLORE_RANK_VALID.has(v)) {
      console.warn(`[FF] Geçersiz değer: ${value}. İzinli: off | on`);
      return;
    }
    try {
      window.localStorage.setItem("mylasa.ff.exploreRank", v);
      console.info(`[FF] exploreRank = ${v} (sayfayı yenile)`);
    } catch (e) {
      console.warn("[FF] localStorage yazılamadı", e);
    }
  };
}

// ─── Keşfet'te bağımsız klipler ──────────────────────────────────────────
// "off" → grid yalnız posts (bugünkü). "on" → global public ready klipler de
// grid'e enjekte edilir (fetchDiscoverPage; yeni index YOK). Anonim/flag-off →
// mevcut davranış. Pre-launch → default "on" (IG-Explore paritesi).
const EXPLORE_CLIPS_VALID = new Set(["off", "on"]);
const EXPLORE_CLIPS_DEFAULT = "on";

export function getExploreClipsEnabled(): boolean {
  const raw =
    _readLocalStorageKey("exploreClips") ||
    _readEnvKey("exploreClips") ||
    EXPLORE_CLIPS_DEFAULT;
  const val = String(raw).toLowerCase();
  const safe = EXPLORE_CLIPS_VALID.has(val) ? val : EXPLORE_CLIPS_DEFAULT;
  return safe === "on";
}

if (typeof window !== "undefined") {
  (window as any).mylasaSetExploreClips = (value: unknown) => {
    const v = String(value || "").toLowerCase();
    if (!EXPLORE_CLIPS_VALID.has(v)) {
      console.warn(`[FF] Geçersiz değer: ${value}. İzinli: off | on`);
      return;
    }
    try {
      window.localStorage.setItem("mylasa.ff.exploreClips", v);
      console.info(`[FF] exploreClips = ${v} (sayfayı yenile)`);
    } catch (e) {
      console.warn("[FF] localStorage yazılamadı", e);
    }
  };
}
